import { useState, useEffect } from "react";
import { Tag, Gift, Wallet, CheckCircle, X } from "@phosphor-icons/react";

const INPUT = "w-full px-3 py-2 border border-[#E5E7EB] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0F1829]/30 focus:border-[#0F1829] transition-all disabled:bg-[#F3F4F6] disabled:cursor-not-allowed";

const fmt = (v, c = "EUR") =>
  new Intl.NumberFormat("pt-PT", { style: "currency", currency: c || "EUR" }).format(Number(v) || 0);

/**
 * Promo code + Vouchers (Vales) + Credits (Créditos) picker.
 * Mirrors the legacy `/shop/checkout` boxes: each shows "utilizados",
 * "consegue utilizar" and "disponíveis", with Apply/Remove actions.
 *
 * verifyData (from /checkout/verify) is the source of truth:
 *   - ChaveCodigoPromocional / ValorDescontoCP      → promo
 *   - vouchers / valesPermitidos / VouchersExistentes → vales
 *   - credits / creditosPermitidos / CreditosExistentes → créditos
 */
export default function PromoVouchersCredits({
  verifyData,
  promoCode, onPromoCodeChange,
  voucherAmount, onVoucherChange,
  creditsAmount, onCreditsChange,
  currency = "EUR",
  busy = false,
  balances = null,    // optional fallback { vouchers, credits } when verify hasn't run
}) {
  const v = verifyData || {};
  const fallback = balances || { vouchers: 0, credits: 0 };
  const appliedPromo = v.ChaveCodigoPromocional || "";
  const promoDiscount = Number(v.ValorDescontoCP || 0);

  const appliedVouchers = Number(v.vouchers || 0);
  const maxVouchers = Number(v.valesPermitidos ?? fallback.vouchers ?? 0);
  const walletVouchers = Number(v.VouchersExistentes ?? fallback.vouchers ?? 0);

  const appliedCredits = Number(v.credits || 0);
  const maxCredits = Number(v.creditosPermitidos ?? fallback.credits ?? 0);
  const walletCredits = Number(v.CreditosExistentes ?? fallback.credits ?? 0);

  return (
    <section className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="promo-vouchers-credits">
      <PromoBox code={promoCode} onCodeChange={onPromoCodeChange}
        appliedCode={appliedPromo} discount={promoDiscount} currency={currency} busy={busy} />

      <VoucherCreditBox
        kind="voucher" label="Vales" icon={<Gift size={16} />}
        input={voucherAmount} onInput={onVoucherChange}
        applied={appliedVouchers} max={maxVouchers} existing={walletVouchers}
        currency={currency} busy={busy} />

      <VoucherCreditBox
        kind="credits" label="Créditos" icon={<Wallet size={16} />}
        input={creditsAmount} onInput={onCreditsChange}
        applied={appliedCredits} max={maxCredits} existing={walletCredits}
        currency={currency} busy={busy} />
    </section>
  );
}

// ─── Promo code box ───────────────────────────────────────────────────

function PromoBox({ code, onCodeChange, appliedCode, discount, currency, busy }) {
  const [input, setInput] = useState(code || "");
  useEffect(() => { setInput(code || ""); }, [code]);

  const applied = !!appliedCode;

  return (
    <div className={"bg-white rounded-xl border p-4 transition-colors "
      + (applied ? "border-[#0F1829] bg-[#F5F5F2]" : "border-[#E5E7EB]")}
      data-testid="promo-box">
      <div className="flex items-center gap-2 mb-1 text-[#0F1829]">
        <Tag size={16} />
        <p className="text-[13px] font-semibold">Código Promocional</p>
      </div>

      {applied ? (
        <div className="mt-1 space-y-1">
          <div className="flex items-center gap-2">
            <CheckCircle size={14} weight="fill" className="text-green-600" />
            <span className="text-xs font-mono font-semibold text-[#0F1829]" data-testid="promo-applied-code">{appliedCode}</span>
          </div>
          <p className="text-[11px] text-[#6B7280]">
            Desconto: <span className="text-[#0F1829] font-semibold">{fmt(discount, currency)}</span>
          </p>
          <button type="button" onClick={() => { setInput(""); onCodeChange?.(""); }}
            disabled={busy} data-testid="promo-remove"
            className="mt-1 text-[11px] text-[#FF2B2B] hover:underline flex items-center gap-1 disabled:opacity-50">
            <X size={12} /> Remover código
          </button>
        </div>
      ) : (
        <div className="mt-1 space-y-2">
          <p className="text-[11px] text-[#6B7280]">Nenhum código utilizado.</p>
          <div className="flex gap-2">
            <input className={INPUT} placeholder="Introduza o código"
              value={input} onChange={(e) => setInput(e.target.value)}
              onBlur={() => { const v = input.trim(); if (v !== code) onCodeChange?.(v); }}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onCodeChange?.(input.trim()); } }}
              data-testid="promo-input" disabled={busy} />
            <button type="button"
              onClick={() => onCodeChange?.(input.trim())}
              disabled={busy || !input.trim()}
              data-testid="promo-apply"
              className="px-3 py-2 bg-[#0F1829] text-white text-xs font-medium rounded-md hover:bg-[#002480] disabled:opacity-50 transition-colors whitespace-nowrap">
              Usar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Voucher/Credits box (same shape) ─────────────────────────────────

function VoucherCreditBox({ kind, label, icon, input, onInput, applied, max, existing, currency, busy }) {
  const disabled = existing === 0;
  const hasApplied = applied > 0;
  const [local, setLocal] = useState("");
  useEffect(() => { setLocal(input > 0 ? String(input) : ""); }, [input]);

  const submit = () => {
    const raw = (local || "").replace(",", ".").trim();
    const n = Number(raw);
    if (!isFinite(n) || n < 0) return;
    const capped = Math.min(n, max);
    onInput?.(capped);
  };

  return (
    <div className={"bg-white rounded-xl border p-4 transition-colors "
      + (hasApplied ? "border-[#0F1829] bg-[#F5F5F2]" : "border-[#E5E7EB]")
      + (disabled ? " opacity-60" : "")}
      data-testid={`${kind}-box`}>
      <div className="flex items-center gap-2 mb-2 text-[#0F1829]">
        {icon}
        <p className="text-[13px] font-semibold">{label}</p>
      </div>

      {/* Stats: utilizados / consegue utilizar / disponíveis */}
      <div className="space-y-0.5 text-[11px] text-[#6B7280] mb-3">
        <Row label="Utilizados" value={fmt(applied, currency)} highlight={hasApplied} testid={`${kind}-used`} />
        <Row label="Consegue utilizar" value={fmt(max, currency)} testid={`${kind}-max`} />
        <Row label="Disponíveis" value={fmt(existing, currency)} testid={`${kind}-existing`} />
      </div>

      {hasApplied ? (
        <button type="button" onClick={() => onInput?.(0)} disabled={busy}
          data-testid={`${kind}-remove`}
          className="w-full text-[11px] text-[#FF2B2B] hover:underline flex items-center justify-center gap-1 disabled:opacity-50">
          <X size={12} /> Remover
        </button>
      ) : (
        <div className="flex gap-2">
          <input type="text" inputMode="decimal"
            className={INPUT} placeholder="0,00"
            value={local} onChange={(e) => setLocal(e.target.value.replace(/[^0-9.,]/g, ""))}
            onBlur={submit}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } }}
            disabled={busy || disabled} data-testid={`${kind}-input`} />
          <button type="button" onClick={submit}
            disabled={busy || disabled || !local.trim()}
            data-testid={`${kind}-apply`}
            className="px-3 py-2 bg-[#0F1829] text-white text-xs font-medium rounded-md hover:bg-[#002480] disabled:opacity-50 transition-colors whitespace-nowrap">
            Usar
          </button>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, highlight = false, testid }) {
  return (
    <div className="flex justify-between">
      <span>{label}:</span>
      <span className={highlight ? "font-semibold text-[#0F1829]" : "text-[#0F1829]"} data-testid={testid}>{value}</span>
    </div>
  );
}
